import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ThunkCloseClientPortalChat,
  ThunkCreateClientPortalChat,
  ThunkGetClientPortalChatMessages,
  ThunkGetClientPortalConfigurations,
  ThunkSendClientPortalChatMessage,
  ThunkGetClientPortalChats,
} from 'store/clientPortalChats/thunks';
import { IChatMessage } from 'components/ClientPortalChats/types';
import { RootState } from 'store/index';
import { IClientPortalChat, IClientPortalConfiguration } from 'services/pod/types';

interface IInitialState {
  messages: IChatMessage[];
  clientPortalChats: IClientPortalChat[];
  clientPortalConfigurations: IClientPortalConfiguration[];
}

const initialState: IInitialState = {
  messages: [],
  clientPortalChats: [],
  clientPortalConfigurations: [],
};

const clientPortalChats = createSlice({
  name: 'clientPortalChats',
  reducers: {
    addClientPortalChatMessage: (state, action: PayloadAction<IChatMessage>) => {
      const existingMessage = state.messages.find(msg => String(msg.id) === String(action.payload.id));
      if (!existingMessage) {
        state.messages.push(action.payload);
      }
    },
    setClientPortalChatClosed: (state, action: PayloadAction<number>) => {
      state.clientPortalChats = state.clientPortalChats.map(chat =>
        +chat.id === +action.payload ? { ...chat, isClosed: true } : chat,
      );
    },
    addClientPortalChat: (state, action: PayloadAction<IClientPortalChat>) => {
      if (state.clientPortalChats.find(i => i.id === action.payload.id)) {
        state.clientPortalChats = state.clientPortalChats.map(i =>
          i.id === action.payload.id ? action.payload : i,
        );
      } else {
        state.clientPortalChats.push(action.payload);
      }
    },
  },
  initialState,
  extraReducers: builder => {
    builder.addCase(ThunkGetClientPortalChatMessages.fulfilled, (state, action) => {
      state.messages = action.payload;
    });
    builder.addCase(ThunkSendClientPortalChatMessage.fulfilled, (state, action) => {
      const { messageId, ...rest } = action.payload;

      const messageIndexByTempId = state.messages.findIndex(i => String(i.id) === String(messageId));

      const messageIndexByRealId = state.messages.findIndex(i => String(i.id) === String(rest.id));

      if (messageIndexByTempId !== -1) {
        state.messages[messageIndexByTempId] = rest;
      } else if (messageIndexByRealId !== -1) {
        state.messages[messageIndexByRealId] = rest;
      } else {
        state.messages.push(rest);
      }
    });
    builder.addCase(ThunkGetClientPortalChats.fulfilled, (state, action) => {
      state.clientPortalChats = action.payload;
    });
    builder.addCase(ThunkCreateClientPortalChat.fulfilled, (state, action) => {
      state.clientPortalChats = [...state.clientPortalChats, action.payload];
    });
    builder.addCase(ThunkCloseClientPortalChat.fulfilled, (state, action) => {
      state.clientPortalChats = state.clientPortalChats.map(chat =>
        chat.id === action.payload ? { ...chat, isClosed: true } : chat,
      );
    });
    builder.addCase(ThunkGetClientPortalConfigurations.fulfilled, (state, action) => {
      state.clientPortalConfigurations = action.payload;
    });
  },
});

export default clientPortalChats.reducer;

export const ClientPortalMessagesSelector = (state: RootState): IChatMessage[] =>
  state.clientPortalChats.messages;
export const ClientPortalChatsSelector = (state: RootState): IClientPortalChat[] =>
  state.clientPortalChats.clientPortalChats;
export const ClientPortalConfigurationsSelector = (
  state: RootState,
): IClientPortalConfiguration[] => state.clientPortalChats.clientPortalConfigurations;

export const { addClientPortalChatMessage, addClientPortalChat, setClientPortalChatClosed } =
  clientPortalChats.actions;
